/**
 * @file 订阅与开票弹窗
 * @description
 * 「订阅管理」与「订单记录」共用同一弹窗，以 Tab 形式切换。
 * 通过 defaultTab prop 控制打开时默认激活的 Tab。
 *
 * 目前仅 SideBar 一处入口，采用 prop 驱动，由调用方管理 open 状态，
 */

import {useCallback, useEffect, useState} from 'react';
import styles from './index.module.less';
import OrderPanel from './OrderPanel';
import SubscriptionPanel from './SubscriptionPanel';
import Modal from '@/components/common/Modal';
import Tabs from '@/components/common/Tabs';
import {createCx} from '@/utils/cx';
import {TrackEvents} from '@/services/tracking/constant';
import {useMemberExtShowLog} from '@/hooks/memberLogExt';

const cx = createCx(styles);

/** 订阅与开票弹窗 Tab 枚举 */
export const SubscriptionOrderTabValue = {
    /** 订阅管理 */
    SUBSCRIPTION: 'subscription',
    /** 订单记录 */
    ORDER: 'order',
} as const;

export type SubscriptionOrderTab =
    (typeof SubscriptionOrderTabValue)[keyof typeof SubscriptionOrderTabValue];

export interface SubscriptionOrderModalProps {
    open: boolean;
    onClose: () => void;
    /** 打开时默认激活的 Tab，默认 SUBSCRIPTION */
    defaultTab?: SubscriptionOrderTab;
}

const TAB_ITEMS: Array<{key: SubscriptionOrderTab; label: string}> = [
    {key: SubscriptionOrderTabValue.SUBSCRIPTION, label: '订阅管理'},
    {key: SubscriptionOrderTabValue.ORDER, label: '订单记录'},
];

/** 埋点用 subscription_mgmt_type 值：与 Tab 一一对应 */
const SUBSCRIPTION_MGMT_TYPE_MAP: Record<SubscriptionOrderTab, string> = {
    [SubscriptionOrderTabValue.SUBSCRIPTION]: 'subscription_management',
    [SubscriptionOrderTabValue.ORDER]: 'order_history',
};

const SubscriptionOrderModal: React.FC<SubscriptionOrderModalProps> = ({
    open,
    onClose,
    defaultTab = SubscriptionOrderTabValue.SUBSCRIPTION,
}) => {
    const [activeTab, setActiveTab] = useState<SubscriptionOrderTab>(defaultTab);

    // 每次打开弹窗时重置为 defaultTab，避免复用上次会话的 Tab 选择
    useEffect(() => {
        if (open) {
            setActiveTab(defaultTab);
        }
    }, [open, defaultTab]);

    // 订阅管理/订单记录是两个独立曝光位（各自身份固定），按身份拆开调用
    useMemberExtShowLog(
        open &&
            activeTab === SubscriptionOrderTabValue.SUBSCRIPTION && {
                page: TrackEvents.SUBSCRIPTION_MANAGEMENT_POP,
                value: TrackEvents.SUBSCRIPTION_MANAGEMENT_POP,
                // eslint-disable-next-line camelcase
                ext: {subscription_mgmt_type: SUBSCRIPTION_MGMT_TYPE_MAP[activeTab]},
            },
    );

    useMemberExtShowLog(
        open &&
            activeTab === SubscriptionOrderTabValue.ORDER && {
                page: TrackEvents.SUBSCRIPTION_MANAGEMENT_POP,
                value: TrackEvents.SUBSCRIPTION_MANAGEMENT_POP,
                // eslint-disable-next-line camelcase
                ext: {subscription_mgmt_type: SUBSCRIPTION_MGMT_TYPE_MAP[activeTab]},
            },
    );

    const handleTabChange = useCallback((key: string) => {
        setActiveTab(key as SubscriptionOrderTab);
    }, []);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            destroyOnHidden
            size="lg"
            className={styles.modal}
            title="订阅与开票"
            footer={null}
        >
            <div className={cx('container')}>
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={TAB_ITEMS.map(({key, label}) => ({key, label}))}
                    className={cx('tabs')}
                    classNames={{item: cx('tabItem')}}
                />
                <div className={cx('panel')}>
                    {activeTab === SubscriptionOrderTabValue.SUBSCRIPTION && (
                        <SubscriptionPanel enabled={open} />
                    )}
                    {activeTab === SubscriptionOrderTabValue.ORDER && <OrderPanel enabled={open} />}
                </div>
            </div>
        </Modal>
    );
};

export default SubscriptionOrderModal;
